import React from 'react';
import { Badge, Box, HStack, Skeleton, Spinner, Stack, Tooltip } from '@chakra-ui/react';
import { Outlet, useParams } from 'react-router-dom';
import { PageHeaderLink } from '~/components/Layout/PageHeader';
import { Projekttyp } from '~/gql/ucpw/graphql';
import { PageHeader } from '~/components/Layout/PageHeader';
import { useSubproject } from '../hooks/useSubproject';
import { useProjectIds } from '~/pages/projects/hooks/useProjectIds';
import { useBrowseHistory } from '~/pages/projects/hooks/useBrowseHistory';
import { useSubProjectMetadata } from '~/pages/projects/hooks/useSubProjectMetadata';
import {
    auftragstypFromMetadata,
    connectionFromMetadata,
    insurerFromMetadata,
    serviceRequestIdFromMetadata,
    sourceMetadataFromSubprojects,
    sourceSystemFromMetadata,
} from '~/meta/data/px-connect';
import { ServiceRequestOriginView } from '~/components/Renderers';
import log from '~/log';

export function Project() {
    const { projectId, subprojectId } = useProjectIds();

    const {
        data: subproject,
        title,
        subprojectNo,
        titleIsLoading,
    } = useSubproject({
        projectId,
        subprojectId: subprojectId || projectId,
    });
    useBrowseHistory(subprojectNo, subproject);

    // Badge-Quelle ist listSubprojectMetadata (nur id + metadata), nicht die Subprojekt-Query:
    // deren Variablen wechseln beim Tab-Wechsel und sie lädt `network-only`, das Badge würde
    // also bei jedem Navigieren kurz verschwinden. Hier bleibt projectId die einzige Variable.
    // Details/Dokumente/… zeigen das aktive Subprojekt, der Projektüberblick (kein subprojectId)
    // das erste Subprojekt mit Integrations-Metadaten — ein Projekt selbst trägt keine.
    const { subprojects, projectMetadata, loading: metadataLoading } = useSubProjectMetadata();
    // Das Badge ist eine PROJEKT-Aussage, keine Subprojekt-Aussage: es steht im Seitenkopf
    // und bleibt über alle Reiter stehen. Trägt das aktive Subprojekt keine Herkunft — weil
    // es von Hand angelegt wurde, etwa eine Nachortung —, fällt es deshalb auf die Herkunft
    // des Projekts zurück, statt zu verschwinden (PXC-B-04).
    //
    // Der Schadenbericht macht das BEWUSST nicht: er braucht die `serviceRequestId` des
    // Subprojekts, weil sie den Fragenkatalog schlüsselt. Ein Rückfall auf „das erste
    // Subprojekt mit Herkunft" zeigte dort den Katalog eines fremden Auftrags — siehe
    // OperationalReportList.tsx:30-32.
    const metadata =
        (subprojectId ? subprojects.find((s) => s.id === subprojectId)?.metadata : undefined) ??
        sourceMetadataFromSubprojects(subprojects) ??
        projectMetadata;
    const serviceRequestId = serviceRequestIdFromMetadata(metadata);
    const sourceSystem = sourceSystemFromMetadata(metadata);
    // Dieselbe Herkunft wie im Auftragseingang (UCPWO-398). Projekte von vor dem Stempel
    // tragen weder Versicherung noch Verbindung — dann steht nur die Integration da.
    const insurer = insurerFromMetadata(metadata);
    const connection = connectionFromMetadata(metadata);
    // UCPWO-410 — der Projekttyp wurde bei der Annahme geraten. Anders als die Herkunft
    // gehört das zu genau EINEM Subprojekt: kein Rückfall auf ein anderes, sonst erbte eine
    // von Hand angelegte Nachortung den Hinweis. Und nur, solange der Typ noch Leckortung
    // ist — wer ihn korrigiert hat, braucht den Hinweis nicht mehr.
    const auftragstyp = auftragstypFromMetadata(
        subprojectId
            ? subprojects.find((s) => s.id === subprojectId)?.metadata
            : sourceMetadataFromSubprojects(subprojects)
    );
    const ersatzweise = auftragstyp?.rueckfall && subproject?.projekttyp?.name === 'Leckortung';

    const activeSubprojectId = subproject?.id;
    const projectType = subproject?.projekttyp as Projekttyp;
    const projectIds = React.useMemo(
        () => ({
            projectId,
            subprojectId: subprojectId || activeSubprojectId,
            version: subproject?.version,
        }),
        [activeSubprojectId, subprojectId, subproject?.version, projectId]
    );

    const pageHeadertitle = titleIsLoading ? (
        <Box>
            Projekt - <Spinner />
        </Box>
    ) : (
        title
    );

    return (
        <>
            <PageHeader>
                <PageHeader.Title>{pageHeadertitle}</PageHeader.Title>
                <PageHeader.Navigation>
                    <Navigation {...projectIds} projectType={projectType} />
                </PageHeader.Navigation>
                {/* Solange die Metadaten laden, ist „kein Badge" noch keine Aussage über die
                    Herkunft — Platzhalter statt eines vorschnellen „nicht PX Connect". */}
                {metadataLoading ? (
                    <PageHeader.Actions>
                        <Skeleton
                            height="1.2em"
                            width="6em"
                            data-test-id="badge-service-request-id-loading"
                        />
                    </PageHeader.Actions>
                ) : (
                    serviceRequestId && (
                        <PageHeader.Actions>
                            <Box data-test-id="badge-service-request-id">
                                {sourceSystem ? (
                                    <HStack spacing={2}>
                                        <ServiceRequestOriginView
                                            insurer={insurer}
                                            sourceSystem={{ source: sourceSystem }}
                                            connection={connection}
                                        />
                                        {ersatzweise && (
                                            <Tooltip
                                                hasArrow
                                                label={
                                                    auftragstyp?.roh
                                                        ? `PX Connect meldete den Auftragstyp „${auftragstyp.roh}“. Das Projekt wurde ersatzweise als Leckortung angelegt – bitte prüfen.`
                                                        : 'Der Auftrag kam ohne Auftragstyp. Das Projekt wurde ersatzweise als Leckortung angelegt – bitte prüfen.'
                                                }
                                            >
                                                <Badge
                                                    colorScheme="red"
                                                    whiteSpace="nowrap"
                                                    data-test-id="badge-projekttyp-ersatzweise"
                                                >
                                                    Projekttyp ersatzweise
                                                </Badge>
                                            </Tooltip>
                                        )}
                                    </HStack>
                                ) : (
                                    serviceRequestId
                                )}
                            </Box>
                        </PageHeader.Actions>
                    )
                )}
            </PageHeader>
            <Outlet context={{ ...projectIds, subproject, title }} />
        </>
    );
}

type ProjectNavigationProps = {
    projectId?: string | number;
    subprojectId?: string | number;
    projectType?: Projekttyp;
};

export function Navigation({ projectId, subprojectId, projectType }: ProjectNavigationProps) {
    const isLeckortung = projectType?.name === 'Leckortung';

    const links = React.useMemo(() => {
        const links = projectLinks({ projectId, subprojectId, projectType }).filter(
            (link) => !link.path.includes('undefined')
        );
        log.debug('ProjectNavigation.links', { projectId, subprojectId, projectType, links });
        return links;
    }, [projectId, subprojectId, projectType]);

    return (
        <Stack direction="row" data-test-id="page-header-links">
            {links.map((link) => (
                <PageHeaderLink
                    data-test-id={`link-${link.id}`}
                    end={!link.path.includes('kalkulation')}
                    key={link.path}
                    to={{ pathname: link.path }}
                >
                    {isLeckortung && link?.title === 'Einsatzberichte'
                        ? 'Leckageortungen'
                        : link.title}
                </PageHeaderLink>
            ))}
        </Stack>
    );
}

const projectLinks = ({ projectId, subprojectId, projectType }: ProjectNavigationProps) => {
    return [
        { id: `overview`, title: `Projektüberblick`, path: `/projekte/${projectId}` },
        { id: `details`, title: `Details`, path: `/projekte/${projectId}/${subprojectId}/details` },
        {
            id: `documents`,
            title: `Dokumente`,
            path: `/projekte/${projectId}/${subprojectId}/dokumente`,
        },
        {
            id: `allowance`,
            title: `Aufmaß`,
            path: `/projekte/${projectId}/${subprojectId}/aufmass`,
        },
        {
            id: `reports`,
            title: `Einsatzberichte`,
            path: `/projekte/${projectId}/${subprojectId}/einsatzberichte`,
            enabled: projectType && ['Leckortung'].includes(projectType?.name),
        },
        {
            id: `calculation`,
            title: `Kalkulation`,
            path: `/projekte/${projectId}/${subprojectId}/kalkulation`,
        },
        {
            id: `deviceUsage`,
            title: `Geräteeinsatz`,
            path: `/projekte/${projectId}/${subprojectId}/geraeteeinsatz`,
            enabled: projectType && ['Trocknung', 'Geräteverleih'].includes(projectType?.name),
        },
    ].filter((link) => link.enabled === undefined || link.enabled);
};
